import * as React from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, Archive, ArchiveRestore, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CHOICE_KINDS, QUESTION_KINDS, slugify, type QuestionKind } from "@poji/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckboxField, Input, Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { BackLink } from "@/components/back-link";
import { DataTable } from "@/components/data-table";
import { ErrorState, LoadingRows, Mono, PageHeader, errorText } from "@/components/common";
import { api, newIdempotencyKey } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------- API biçimi

interface FormRow {
  id: string;
  code: string;
  kind: "stepwise" | "questionnaire";
  version: number;
  isActive: boolean;
  questionCount: number;
  services: string[];
}
interface Option {
  id: string;
  code: string;
  label: string | null;
  estimatedMinutes: number | null;
  acceptsText: boolean;
  routesTo: string | null;
  isActive: boolean;
  answerCount: number;
}
interface Question {
  id: string;
  code: string;
  kind: QuestionKind;
  section: string | null;
  isRequired: boolean;
  minValue: number | null;
  maxValue: number | null;
  maxLength: number | null;
  visibleIf: unknown;
  isActive: boolean;
  label: string | null;
  helpText: string | null;
  placeholder: string | null;
  locales: string[];
  answerCount: number;
  options: Option[];
}
interface FormDetail {
  form: { id: string; code: string; kind: string; version: number; isActive: boolean };
  locale: string;
  services: { id: string; code: string; isActive: boolean }[];
  questions: Question[];
}

export const KIND_LABEL: Record<QuestionKind, string> = {
  address: "Adres",
  secondary_address: "İkinci adres (varış)",
  task_size: "İş büyüklüğü",
  vehicle: "Araç gereksinimi",
  description: "Açıklama (serbest metin)",
  single_choice: "Tek seçim",
  multi_choice: "Çoklu seçim",
  text: "Kısa metin",
  long_text: "Uzun metin",
  number: "Sayı",
  date: "Tarih",
  routing: "Yönlendirme (başka hizmete)",
};
const toCode = (s: string) =>
  slugify(s)
    .replace(/-/g, "_")
    .replace(/^[^a-z]+/, "")
    .slice(0, 60);
const isChoice = (k: QuestionKind) => CHOICE_KINDS.includes(k);

// ---------------------------------------------------------------- liste

function NewFormDialog({ forms }: { forms: FormRow[] }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [code, setCode] = React.useState("");
  const [kind, setKind] = React.useState<FormRow["kind"]>("stepwise");
  const [copy, setCopy] = React.useState("");
  return (
    <ActionDialog
      onOpen={() => {
        setCode("");
        setKind("stepwise");
        setCopy("");
      }}
      trigger={
        <Button size="sm">
          <Plus /> Yeni form
        </Button>
      }
      title="Yeni form"
      description="Benzer bir formu kopyalamak en hızlısı: sorular, seçenekler ve çevirileri gelir, sonra düzenlersiniz. Formu bir hizmete Hizmetler ekranından bağlarsınız."
      confirmLabel="Oluştur"
      canSubmit={/^[a-z][a-z0-9_]{0,62}$/.test(code)}
      success="Form oluşturuldu."
      onSubmit={async (key) => {
        const r = await api<{ id: string }>("/v1/admin/forms", {
          method: "POST",
          idempotencyKey: key,
          body: { code, kind, copyFromId: copy || null },
        });
        await qc.invalidateQueries({ queryKey: ["admin-forms"] });
        navigate(`/forms/${r.id}`);
      }}
    >
      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="nf-code">Kod</Label>
          <Input
            id="nf-code"
            placeholder="ör. pet_care"
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase())}
          />
          <p className="text-xs text-muted-foreground">
            Küçük harf, rakam, alt çizgi. Sonradan değişmez.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="nf-copy">Kopyalanacak form</Label>
            <NativeSelect id="nf-copy" value={copy} onChange={(e) => setCopy(e.target.value)}>
              <option value="">Boş başla</option>
              {forms.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.code}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="nf-kind">Görünüm</Label>
            <NativeSelect
              id="nf-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as FormRow["kind"])}
            >
              <option value="stepwise">Adım adım</option>
              <option value="questionnaire">Tek sayfada soru listesi</option>
            </NativeSelect>
          </div>
        </div>
      </div>
    </ActionDialog>
  );
}

export function FormsPage() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["admin-forms"],
    queryFn: () => api<{ forms: FormRow[] }>("/v1/admin/forms"),
  });
  const columns: ColumnDef<FormRow, unknown>[] = [
    {
      accessorKey: "code",
      header: "Form",
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.code}</div>
          <div className="text-xs text-muted-foreground">
            {row.original.kind === "stepwise" ? "Adım adım" : "Soru listesi"} · sürüm{" "}
            {row.original.version}
          </div>
        </div>
      ),
    },
    { accessorKey: "questionCount", header: "Soru" },
    {
      id: "services",
      header: "Kullanan hizmetler",
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-md text-sm text-muted-foreground">
          {row.original.services.length ? row.original.services.join(", ") : "—"}
        </span>
      ),
    },
    {
      id: "status",
      header: "Durum",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="success">Açık</Badge>
        ) : (
          <Badge variant="secondary">Kapalı</Badge>
        ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Hizmet formları"
        description="Müşterinin iş açarken cevapladığı sorular. Birçok hizmet aynı formu kullanabilir; değişiklik o formu kullanan tüm hizmetlere yansır."
        actions={q.data && <NewFormDialog forms={q.data.forms} />}
      />
      <DataTable
        columns={columns}
        data={q.data?.forms}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        onRowClick={(r) => navigate(`/forms/${r.id}`)}
        empty="Form yok."
      />
    </>
  );
}

// ---------------------------------------------------------------- ayrıntı: soru ekle/düzenle

interface OptionDraft {
  label: string;
  code: string;
  minutes: string;
}

function QuestionDialog({ formId, question }: { formId: string; question?: Question }) {
  const qc = useQueryClient();
  const init = () => ({
    kind: (question?.kind ?? "single_choice") as QuestionKind,
    label: question?.label ?? "",
    code: question?.code ?? "",
    helpText: question?.helpText ?? "",
    placeholder: question?.placeholder ?? "",
    isRequired: question?.isRequired ?? true,
    section: question?.section ?? "",
    minValue: question?.minValue?.toString() ?? "",
    maxValue: question?.maxValue?.toString() ?? "",
    maxLength: question?.maxLength?.toString() ?? "",
    options: [
      { label: "", code: "", minutes: "" },
      { label: "", code: "", minutes: "" },
    ] as OptionDraft[],
  });
  const [f, setF] = React.useState(init);
  const set = (p: Partial<ReturnType<typeof init>>) => setF((x) => ({ ...x, ...p }));
  const numOrNull = (s: string) => (s.trim() === "" ? null : Number(s.replace(",", ".")));
  const code = question ? question.code : f.code || toCode(f.label);
  const opts = f.options
    .filter((o) => o.label.trim())
    .map((o) => ({
      code: o.code || toCode(o.label),
      label: o.label.trim(),
      estimatedMinutes: o.minutes.trim() ? Number(o.minutes) : null,
    }));
  const choice = isChoice(f.kind);
  const canSubmit =
    Boolean(f.label.trim() && code) &&
    (question || !choice || opts.length > 0) &&
    [f.minValue, f.maxValue, f.maxLength].every(
      (s) => s.trim() === "" || !Number.isNaN(numOrNull(s)),
    );

  return (
    <ActionDialog
      wide
      onOpen={() => setF(init())}
      trigger={
        question ? (
          <Button size="sm" variant="ghost">
            <Pencil /> Düzenle
          </Button>
        ) : (
          <Button size="sm">
            <Plus /> Soru ekle
          </Button>
        )
      }
      title={question ? `Soruyu düzenle: ${question.code}` : "Soru ekle"}
      description={
        question
          ? question.answerCount
            ? `${question.answerCount} işte cevaplandı. Metin değişikliği eski işlerde de görünür; soru türü değişmez.`
            : "Soru türü ve kodu değişmez; farklı bir şey soracaksanız yeni soru ekleyin."
          : "Formun sonuna eklenir; sonra okla yerini değiştirebilirsiniz. Metin İngilizce; Maltaca Çeviriler ekranından."
      }
      confirmLabel="Kaydet"
      canSubmit={canSubmit}
      success={question ? "Soru güncellendi." : "Soru eklendi."}
      onSubmit={async (key) => {
        const common = {
          label: f.label.trim(),
          helpText: f.helpText.trim() || null,
          placeholder: f.placeholder.trim() || null,
          isRequired: f.isRequired,
          section: f.section.trim() || null,
          minValue: numOrNull(f.minValue),
          maxValue: numOrNull(f.maxValue),
          maxLength: numOrNull(f.maxLength),
        };
        if (question) {
          // yalnızca değişen alanlar (gereksiz sürüm artışı olmasın)
          const before = {
            label: question.label ?? "",
            helpText: question.helpText,
            placeholder: question.placeholder,
            isRequired: question.isRequired,
            section: question.section,
            minValue: question.minValue,
            maxValue: question.maxValue,
            maxLength: question.maxLength,
          } as Record<string, unknown>;
          const body = Object.fromEntries(
            Object.entries(common).filter(([k, v]) => v !== before[k]),
          );
          if (Object.keys(body).length)
            await api(`/v1/admin/forms/questions/${question.id}`, { method: "PATCH", body });
        } else
          await api(`/v1/admin/forms/${formId}/questions`, {
            method: "POST",
            idempotencyKey: key,
            body: { code, kind: f.kind, ...common, options: choice ? opts : [] },
          });
        await qc.invalidateQueries({ queryKey: ["admin-form", formId] });
        await qc.invalidateQueries({ queryKey: ["admin-forms"] });
      }}
    >
      <div className="grid gap-4">
        {!question && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="q-kind">Soru türü</Label>
              <NativeSelect
                id="q-kind"
                value={f.kind}
                onChange={(e) => set({ kind: e.target.value as QuestionKind })}
              >
                {QUESTION_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="q-code">Kod</Label>
              <Input
                id="q-code"
                value={f.code}
                placeholder={toCode(f.label) || "ör. pets"}
                onChange={(e) => set({ code: e.target.value.toLowerCase() })}
              />
            </div>
          </div>
        )}
        <div className="grid gap-2">
          <Label htmlFor="q-label">Soru</Label>
          <Input
            id="q-label"
            maxLength={300}
            value={f.label}
            placeholder="ör. Do you have pets at home?"
            onChange={(e) => set({ label: e.target.value })}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="q-help">Yardım metni</Label>
            <Textarea
              id="q-help"
              className="min-h-14"
              value={f.helpText}
              onChange={(e) => set({ helpText: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="q-ph">Örnek metin (kutu içinde)</Label>
            <Input
              id="q-ph"
              value={f.placeholder}
              onChange={(e) => set({ placeholder: e.target.value })}
            />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="grid gap-2">
            <Label htmlFor="q-section">Bölüm</Label>
            <Input
              id="q-section"
              placeholder="ör. task"
              value={f.section}
              onChange={(e) => set({ section: e.target.value })}
            />
          </div>
          {(f.kind === "number" || question?.kind === "number") && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="q-min">En az</Label>
                <Input
                  id="q-min"
                  inputMode="decimal"
                  value={f.minValue}
                  onChange={(e) => set({ minValue: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="q-max">En çok</Label>
                <Input
                  id="q-max"
                  inputMode="decimal"
                  value={f.maxValue}
                  onChange={(e) => set({ maxValue: e.target.value })}
                />
              </div>
            </>
          )}
          {["text", "long_text", "description"].includes(question?.kind ?? f.kind) && (
            <div className="grid gap-2">
              <Label htmlFor="q-len">En fazla karakter</Label>
              <Input
                id="q-len"
                inputMode="numeric"
                value={f.maxLength}
                onChange={(e) => set({ maxLength: e.target.value })}
              />
            </div>
          )}
        </div>
        <CheckboxField
          label="Zorunlu"
          checked={f.isRequired}
          onChange={(e) => set({ isRequired: e.target.checked })}
        />

        {!question && choice && (
          <div className="grid gap-2">
            <Label>Seçenekler</Label>
            {f.options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  aria-label={`Seçenek ${i + 1}`}
                  placeholder={`Seçenek ${i + 1}`}
                  value={o.label}
                  onChange={(e) =>
                    set({
                      options: f.options.map((x, j) =>
                        j === i ? { ...x, label: e.target.value } : x,
                      ),
                    })
                  }
                />
                {f.kind === "task_size" && (
                  <Input
                    aria-label={`Seçenek ${i + 1} tahmini dakika`}
                    className="w-28"
                    inputMode="numeric"
                    placeholder="dk"
                    value={o.minutes}
                    onChange={(e) =>
                      set({
                        options: f.options.map((x, j) =>
                          j === i ? { ...x, minutes: e.target.value } : x,
                        ),
                      })
                    }
                  />
                )}
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="Seçeneği sil"
                  disabled={f.options.length <= 1}
                  onClick={() => set({ options: f.options.filter((_, j) => j !== i) })}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="justify-self-start"
              onClick={() => set({ options: [...f.options, { label: "", code: "", minutes: "" }] })}
            >
              <Plus /> Seçenek
            </Button>
          </div>
        )}
      </div>
    </ActionDialog>
  );
}

function AddOptionDialog({ formId, question }: { formId: string; question: Question }) {
  const qc = useQueryClient();
  const [label, setLabel] = React.useState("");
  const [minutes, setMinutes] = React.useState("");
  return (
    <ActionDialog
      onOpen={() => {
        setLabel("");
        setMinutes("");
      }}
      trigger={
        <Button size="sm" variant="ghost">
          <Plus /> Seçenek
        </Button>
      }
      title={`Seçenek ekle: ${question.label ?? question.code}`}
      confirmLabel="Ekle"
      canSubmit={Boolean(label.trim())}
      success="Seçenek eklendi."
      onSubmit={async (key) => {
        await api(`/v1/admin/forms/questions/${question.id}/options`, {
          method: "POST",
          idempotencyKey: key,
          body: {
            code: toCode(label),
            label: label.trim(),
            estimatedMinutes: minutes.trim() ? Number(minutes) : null,
          },
        });
        await qc.invalidateQueries({ queryKey: ["admin-form", formId] });
      }}
    >
      <div className="grid gap-3">
        <Label htmlFor="ao-label">Seçenek</Label>
        <Input id="ao-label" value={label} onChange={(e) => setLabel(e.target.value)} />
        {question.kind === "task_size" && (
          <>
            <Label htmlFor="ao-min">Tahmini süre (dk)</Label>
            <Input
              id="ao-min"
              inputMode="numeric"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </>
        )}
      </div>
    </ActionDialog>
  );
}

/** Küçük işlemler (sıra, kaldır/geri al): onaysız; hata bildirimle */
function useQuick(formId: string) {
  const qc = useQueryClient();
  const [busy, setBusy] = React.useState(false);
  return {
    busy,
    run: async (path: string, method: "POST" | "PATCH", body: unknown, ok?: string) => {
      setBusy(true);
      try {
        await api(path, {
          method,
          body,
          ...(method === "POST" ? { idempotencyKey: newIdempotencyKey() } : {}),
        });
        await qc.invalidateQueries({ queryKey: ["admin-form", formId] });
        if (ok) toast.success(ok);
      } catch (e) {
        toast.error(errorText(e));
      } finally {
        setBusy(false);
      }
    },
  };
}

function OptionRow({
  formId,
  option,
  first,
  last,
}: {
  formId: string;
  option: Option;
  first: boolean;
  last: boolean;
}) {
  const quick = useQuick(formId);
  return (
    <li
      className={cn(
        "flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted/60",
        !option.isActive && "opacity-60",
      )}
    >
      <span className={cn("flex-1", !option.isActive && "line-through")}>
        {option.label ?? <span className="text-destructive">metin yok</span>}{" "}
        <Mono>{option.code}</Mono>
        {option.estimatedMinutes !== null && (
          <span className="ml-2 text-xs text-muted-foreground">~{option.estimatedMinutes} dk</span>
        )}
        {option.routesTo && (
          <span className="ml-2 text-xs text-muted-foreground">→ {option.routesTo}</span>
        )}
      </span>
      {option.answerCount > 0 && (
        <span className="text-xs text-muted-foreground">{option.answerCount} iş</span>
      )}
      <Button
        size="icon"
        variant="ghost"
        className="size-7"
        aria-label={`${option.code} yukarı`}
        disabled={first || quick.busy}
        onClick={() =>
          void quick.run(`/v1/admin/forms/options/${option.id}/move`, "POST", { direction: "up" })
        }
      >
        <ArrowUp />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="size-7"
        aria-label={`${option.code} aşağı`}
        disabled={last || quick.busy}
        onClick={() =>
          void quick.run(`/v1/admin/forms/options/${option.id}/move`, "POST", { direction: "down" })
        }
      >
        <ArrowDown />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="size-7"
        aria-label={option.isActive ? `${option.code} kaldır` : `${option.code} geri al`}
        title={option.isActive ? "Kaldır (eski cevaplar kalır)" : "Geri al"}
        disabled={quick.busy}
        onClick={() =>
          void quick.run(
            `/v1/admin/forms/options/${option.id}`,
            "PATCH",
            { isActive: !option.isActive },
            option.isActive ? "Seçenek kaldırıldı." : "Seçenek geri alındı.",
          )
        }
      >
        {option.isActive ? <Archive /> : <ArchiveRestore />}
      </Button>
    </li>
  );
}

function QuestionCard({
  formId,
  q,
  index,
  first,
  last,
  locales,
}: {
  formId: string;
  q: Question;
  index: number;
  first: boolean;
  last: boolean;
  locales: string[];
}) {
  const quick = useQuick(formId);
  const missing = locales.filter((l) => !q.locales.includes(l));
  return (
    <Card className={cn(!q.isActive && "border-dashed opacity-70")}>
      <CardContent className="grid gap-3 p-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="grid size-7 shrink-0 place-items-center rounded-md bg-muted text-xs font-medium tabular">
            {index}
          </div>
          <div className="min-w-0 flex-1">
            <div className={cn("font-medium", !q.isActive && "line-through")}>
              {q.label ?? <span className="text-destructive">metin yok</span>}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
              <Badge variant="outline">{KIND_LABEL[q.kind] ?? q.kind}</Badge>
              <Badge variant={q.isRequired ? "default" : "secondary"}>
                {q.isRequired ? "zorunlu" : "isteğe bağlı"}
              </Badge>
              {!q.isActive && <Badge variant="warning">kaldırıldı</Badge>}
              <Mono>{q.code}</Mono>
              {q.section && <span className="text-muted-foreground">· bölüm {q.section}</span>}
              {q.answerCount > 0 && (
                <span className="text-muted-foreground">· {q.answerCount} işte cevaplandı</span>
              )}
              {missing.length > 0 && (
                <Link to="/translations" className="hover:underline">
                  <Badge variant="warning">çeviri eksik: {missing.join(", ")}</Badge>
                </Link>
              )}
              {q.visibleIf != null && (
                <span className="text-muted-foreground" title={JSON.stringify(q.visibleIf)}>
                  · koşullu gösterim
                </span>
              )}
            </div>
            {q.helpText && <p className="mt-1 text-sm text-muted-foreground">{q.helpText}</p>}
          </div>
          <div className="flex items-center gap-0.5">
            <Button
              size="icon"
              variant="ghost"
              aria-label={`${q.code} yukarı`}
              disabled={first || quick.busy}
              onClick={() =>
                void quick.run(`/v1/admin/forms/questions/${q.id}/move`, "POST", {
                  direction: "up",
                })
              }
            >
              <ArrowUp />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`${q.code} aşağı`}
              disabled={last || quick.busy}
              onClick={() =>
                void quick.run(`/v1/admin/forms/questions/${q.id}/move`, "POST", {
                  direction: "down",
                })
              }
            >
              <ArrowDown />
            </Button>
            <QuestionDialog formId={formId} question={q} />
            <Button
              size="sm"
              variant="ghost"
              disabled={quick.busy}
              onClick={() =>
                void quick.run(
                  `/v1/admin/forms/questions/${q.id}`,
                  "PATCH",
                  { isActive: !q.isActive },
                  q.isActive
                    ? "Soru kaldırıldı: yeni işlerde sorulmaz, eski cevaplar kalır."
                    : "Soru geri alındı.",
                )
              }
            >
              {q.isActive ? (
                <>
                  <Archive /> Kaldır
                </>
              ) : (
                <>
                  <ArchiveRestore /> Geri al
                </>
              )}
            </Button>
          </div>
        </div>
        {isChoice(q.kind) && (
          <div className="ml-10">
            <ul className="grid gap-0.5">
              {q.options.map((o, i) => (
                <OptionRow
                  key={o.id}
                  formId={formId}
                  option={o}
                  first={i === 0}
                  last={i === q.options.length - 1}
                />
              ))}
            </ul>
            {q.isActive && <AddOptionDialog formId={formId} question={q} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function FormDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ["admin-form", id],
    queryFn: () => api<FormDetail>(`/v1/admin/forms/${id}`),
  });
  const locales = useQuery({
    queryKey: ["admin-translations-summary"],
    queryFn: () => api<{ locales: { code: string }[] }>("/v1/admin/translations/summary"),
    enabled: can("catalog.translations.manage"),
  });
  const [showRetired, setShowRetired] = React.useState(false);
  if (q.isPending) return <LoadingRows rows={6} />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  const visible = d.questions.filter((x) => showRetired || x.isActive);
  const retired = d.questions.length - d.questions.filter((x) => x.isActive).length;
  const langs = locales.data?.locales.map((l) => l.code) ?? [d.locale];

  return (
    <>
      <PageHeader
        back={<BackLink to="/forms">Formlar</BackLink>}
        title={
          <span className="flex items-center gap-2">
            {d.form.code}
            <Badge variant="outline">sürüm {d.form.version}</Badge>
            {!d.form.isActive && <Badge variant="secondary">kapalı</Badge>}
          </span>
        }
        description={
          <>
            Kullanan hizmetler:{" "}
            {d.services.length ? d.services.map((s) => s.code).join(", ") : "yok"}. Soru eklemek,
            kaldırmak, sırasını ya da zorunluluğunu değiştirmek sürümü artırır; açılmış işler kendi
            cevaplarıyla kalır.
          </>
        }
        actions={
          <>
            {d.form.isActive && <QuestionDialog formId={d.form.id} />}
            <ActionDialog
              trigger={
                <Button size="sm" variant="outline">
                  {d.form.isActive ? "Formu kapat" : "Formu aç"}
                </Button>
              }
              title={d.form.isActive ? "Formu kapat" : "Formu aç"}
              description={
                d.form.isActive
                  ? "Bu formu kullanan açık hizmet varken kapatılamaz; önce hizmetlere başka form seçin."
                  : "Form yeniden kullanılabilir."
              }
              confirmLabel={d.form.isActive ? "Kapat" : "Aç"}
              success="Kaydedildi."
              onSubmit={async () => {
                await api(`/v1/admin/forms/${d.form.id}`, {
                  method: "PATCH",
                  body: { isActive: !d.form.isActive },
                });
                await qc.invalidateQueries({ queryKey: ["admin-form", id] });
                await qc.invalidateQueries({ queryKey: ["admin-forms"] });
              }}
            />
          </>
        }
      />
      {retired > 0 && (
        <CheckboxField
          className="mb-3"
          label={`Kaldırılan soruları da göster (${retired})`}
          checked={showRetired}
          onChange={(e) => setShowRetired(e.target.checked)}
        />
      )}
      <div className="grid gap-3">
        {visible.map((x, i) => (
          <QuestionCard
            key={x.id}
            formId={d.form.id}
            q={x}
            index={i + 1}
            first={i === 0}
            last={i === visible.length - 1}
            locales={langs}
          />
        ))}
      </div>
    </>
  );
}
